import React, { useEffect, useRef, useState } from 'react';
import './TranscriptEditor.css';

export const TRANSCRIPT_FONTS = ['Arial', 'Georgia', 'Times New Roman', 'Verdana', 'monospace'];
export const TRANSCRIPT_SIZES = [12, 14, 16, 18, 20, 24, 28, 32, 40];
const allowedTags = new Set(['p', 'div', 'br', 'h1', 'h2', 'h3', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'span']);
const discardedTags = new Set(['script', 'style', 'iframe', 'object', 'embed', 'svg', 'math', 'template', 'noscript']);
const legacySizes: Record<string, number> = { '1': 12, '2': 14, '3': 16, '4': 18, '5': 24, '6': 32, '7': 40 };

/** Keep only the formatting supported by the server, including browser font commands. */
export function normalizeTranscriptHtml(html: string): string {
  const source = new DOMParser().parseFromString(html, 'text/html');
  const output = document.createElement('div');
  const copy = (node: Node, target: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      target.appendChild(document.createTextNode(node.textContent || ''));
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const element = node as HTMLElement;
    const tag = element.tagName.toLowerCase();
    if (discardedTags.has(tag)) return;
    if (!allowedTags.has(tag) && tag !== 'font') {
      Array.from(element.childNodes).forEach(child => copy(child, target));
      return;
    }
    const clean = document.createElement(tag === 'font' ? 'span' : tag);
    const rawFamily = (element.style.fontFamily || element.getAttribute('face') || '').trim().replace(/^['"]|['"]$/g, '');
    const family = TRANSCRIPT_FONTS.find(item => item.toLowerCase() === rawFamily.toLowerCase());
    if (family) clean.style.fontFamily = family;
    const rawSize = element.style.fontSize || (tag === 'font' ? `${legacySizes[element.getAttribute('size') || ''] || 0}px` : '');
    const size = /^\d+px$/.test(rawSize) ? parseInt(rawSize, 10) : 0;
    if (TRANSCRIPT_SIZES.includes(size)) clean.style.fontSize = `${size}px`;
    const weight = element.style.fontWeight.toLowerCase();
    if (['bold', 'normal'].includes(weight)) clean.style.fontWeight = weight;
    const fontStyle = element.style.fontStyle.toLowerCase();
    if (['italic', 'normal'].includes(fontStyle)) clean.style.fontStyle = fontStyle;
    const decoration = element.style.textDecoration.toLowerCase();
    if (['underline', 'none'].includes(decoration)) clean.style.textDecoration = decoration;
    Array.from(element.childNodes).forEach(child => copy(child, clean));
    target.appendChild(clean);
  };
  Array.from(source.body.childNodes).forEach(node => copy(node, output));
  return output.innerHTML;
}

interface TranscriptEditorProps {
  html: string;
  onSave: (html: string) => Promise<void>;
  onCancel: () => void;
}

const TranscriptEditor: React.FC<TranscriptEditorProps> = ({ html, onSave, onCancel }) => {
  const editor = useRef<HTMLDivElement>(null);
  const selection = useRef<Range | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [font, setFont] = useState('Arial');
  const [fontSize, setFontSize] = useState(16);
  const [block, setBlock] = useState('p');

  useEffect(() => {
    if (editor.current) editor.current.innerHTML = normalizeTranscriptHtml(html);
    selection.current = null;
  }, [html]);

  const rememberSelection = () => {
    const current = window.getSelection();
    if (editor.current && current?.rangeCount && editor.current.contains(current.getRangeAt(0).commonAncestorContainer)) {
      selection.current = current.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    const current = window.getSelection();
    const target = editor.current;
    if (!target || !current) return;
    target.focus();
    const range = selection.current;
    if (range && target.contains(range.commonAncestorContainer)) {
      current.removeAllRanges();
      current.addRange(range);
    } else {
      const end = document.createRange();
      end.selectNodeContents(target);
      end.collapse(false);
      current.removeAllRanges();
      current.addRange(end);
    }
  };

  const command = (name: string, value?: string) => {
    if (saving) return;
    restoreSelection();
    document.execCommand(name, false, value);
    rememberSelection();
    setError('');
  };

  const changeSize = (size: number) => {
    setFontSize(size);
    command('fontSize', '7');
    editor.current?.querySelectorAll('font[size="7"]').forEach(element => {
      // Keep the browser's live formatting node so typing after the selection uses this size.
      element.removeAttribute('size');
      (element as HTMLElement).style.fontSize = `${size}px`;
    });
    rememberSelection();
  };

  const paste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (saving) return;
    rememberSelection();
    restoreSelection();
    const text = event.clipboardData.getData('text/plain');
    const current = window.getSelection();
    if (!current?.rangeCount) return;
    const range = current.getRangeAt(0);
    range.deleteContents();
    const fragment = document.createDocumentFragment();
    text.replace(/\r\n?/g, '\n').split('\n').forEach((line, index) => {
      if (index) fragment.appendChild(document.createElement('br'));
      fragment.appendChild(document.createTextNode(line));
    });
    const last = fragment.lastChild;
    range.insertNode(fragment);
    if (last) range.setStartAfter(last);
    range.collapse(true);
    current.removeAllRanges();
    current.addRange(range);
    rememberSelection();
    setError('');
  };

  const save = async () => {
    if (saving || !editor.current) return;
    const value = normalizeTranscriptHtml(editor.current.innerHTML);
    const plain = new DOMParser().parseFromString(value, 'text/html').body.textContent || '';
    if (!plain.trim()) { setError('Введите текст транскрибации'); return; }
    setSaving(true);
    setError('');
    try { await onSave(value); }
    catch (err: any) { setError(err?.message || 'Не удалось сохранить текст. Ваши изменения остаются в редакторе.'); }
    finally { setSaving(false); }
  };

  return <section data-guide="transcript-editor" className="transcript-editor" aria-label="Редактор транскрибации" aria-busy={saving}>
    <div className="transcript-editor-toolbar" role="toolbar" aria-label="Форматирование текста">
      <label>Шрифт<select aria-label="Шрифт" value={font} disabled={saving} onMouseDown={rememberSelection} onChange={event => {
        setFont(event.target.value); command('fontName', event.target.value);
      }}>{TRANSCRIPT_FONTS.map(value => <option key={value} value={value}>{value === 'monospace' ? 'Моноширинный' : value}</option>)}</select></label>
      <label>Размер<select aria-label="Размер шрифта" value={fontSize} disabled={saving} onMouseDown={rememberSelection} onChange={event => changeSize(Number(event.target.value))}>
        {TRANSCRIPT_SIZES.map(value => <option key={value} value={value}>{value} px</option>)}
      </select></label>
      <label>Стиль<select aria-label="Стиль абзаца" value={block} disabled={saving} onMouseDown={rememberSelection} onChange={event => {
        setBlock(event.target.value); command('formatBlock', event.target.value);
      }}><option value="p">Обычный текст</option><option value="h1">Заголовок 1</option><option value="h2">Заголовок 2</option><option value="h3">Заголовок 3</option></select></label>
      <div className="transcript-editor-marks">
        {[['bold', 'Жирный', 'Ж'], ['italic', 'Курсив', 'К'], ['underline', 'Подчёркнутый', 'Ч'], ['insertUnorderedList', 'Маркированный список', '•'], ['insertOrderedList', 'Нумерованный список', '1.']].map(([name, label, symbol]) =>
          <button type="button" key={name} aria-label={label} title={label} disabled={saving} onMouseDown={event => { event.preventDefault(); rememberSelection(); }} onClick={() => command(name)}>{symbol}</button>)}
      </div>
    </div>
    <div ref={editor} className="transcript-editor-content training-transcript-html" role="textbox" aria-label="Текст транскрибации" aria-multiline="true" contentEditable={!saving} suppressContentEditableWarning
      onInput={() => { rememberSelection(); setError(''); }} onMouseUp={rememberSelection} onKeyUp={rememberSelection} onBlur={rememberSelection}
      onPaste={paste} onDrop={event => event.preventDefault()} />
    {error && <div role="alert" className="transcript-editor-error">{error}</div>}
    <div className="transcript-editor-actions">
      <button type="button" onClick={onCancel} disabled={saving}>Отмена</button>
      <button type="button" className="transcript-editor-save" onClick={save} disabled={saving}>{saving ? 'Сохранение…' : 'Сохранить текст'}</button>
    </div>
  </section>;
};

export default TranscriptEditor;
